import type { Vec3 } from '../math.js';

/**
 * Collision geometry as axis-aligned boxes ("brushes").
 *
 * ADR-0001 separates visual geometry (glTF, authored in Blender) from collision
 * geometry (this). Two reasons it is worth keeping them apart:
 *
 * 1. Server cost. Sweeping a capsule against a handful of boxes is orders of
 *    magnitude cheaper than against a triangle mesh, and the server does that
 *    several times per player per tick, plus again for every rewound hitscan.
 * 2. Determinism and predictability. Box sweeps have stable, well-conditioned
 *    contact normals. Triangle soup produces sliver contacts at seams that make
 *    a player snag on flat floors — the classic "invisible wall" bug.
 *
 * Maps therefore ship a visual mesh and this brush list side by side. Boxes are
 * the only primitive until a map needs more (ramps come with P1's real map).
 *
 * The brush *data* lives in `shared/maps/*.json` and is parsed by
 * `shared/src/map/definition.ts`. This file only defines the shapes: a map baked
 * into TypeScript could not be validated at the boundary where bad data arrives,
 * and content does not belong in code.
 */
export interface BoxBrush {
  kind: 'box';
  center: Vec3;
  /** Half-size on each axis. A 1x1x1 box has halfExtents 0.5. */
  halfExtents: Vec3;
}

export type Brush = BoxBrush;

export interface CollisionMap {
  name: string;
  brushes: Brush[];
  spawns: Vec3[];
}

export function box(center: Vec3, halfExtents: Vec3): BoxBrush {
  return { kind: 'box', center, halfExtents };
}
